/**
 * Loads the AV Simulation Tool logo as a base64 PNG for embedding in jsPDF
 * reports. Result is cached on the module so repeated PDF exports don't
 * re-fetch the asset.
 */
let cachedDataUrl = null;
let inflightPromise = null;

export const getLogoDataUrl = async () => {
  if (cachedDataUrl) return cachedDataUrl;
  if (inflightPromise) return inflightPromise;

  inflightPromise = (async () => {
    try {
      const res = await fetch('/branding/avst-logo-pdf.png');
      const blob = await res.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          cachedDataUrl = reader.result;
          resolve(cachedDataUrl);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      // If the asset can't be loaded, return null and let callers no-op.
      return null;
    } finally {
      inflightPromise = null;
    }
  })();

  return inflightPromise;
};

/**
 * Adds the logo to the top-left of the current jsPDF page header bar.
 * No-ops gracefully if the logo asset isn't available.
 *
 * @param {jsPDF} pdf
 * @param {object} opts - { x, y, height } in mm. Width is auto-derived from
 *   the logo's natural aspect ratio (1536:1024 ≈ 3:2).
 */
export const addLogoToPdfHeader = async (pdf, { x = 8, y = 6, height = 14 } = {}) => {
  const dataUrl = await getLogoDataUrl();
  if (!dataUrl) return 0;
  const aspect = 1.5; // logo is 1536:1024 = 3:2
  const width = height * aspect;
  try {
    pdf.addImage(dataUrl, 'PNG', x, y, width, height);
    return width;
  } catch (_) {
    return 0;
  }
};
